import { UniButton } from '@/design-system/components/buttons';
import { H6, TextBody, TextCaption } from '@/design-system/components/typography';
import { ScrollView, SxProp, View } from 'dripsy';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/design-system/components/icons/Icon';
import { LayoutContext } from 'contexts/LayoutContext';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { AccountsTransfersStackParamList } from '@/features/account-management/screens/accounts-transfers/type';
import moment from 'moment-timezone';
import { formatAmountByDecimal } from '@/features/account-management/utils';

export interface AccountsTransfersProps {
  onClose: () => void;
  data: any;
  onLayout: (event: any) => void;
  account: string;
  tab?: number;
  returnData?: any;
  refresh?: () => void;
  record?: any;
}

export const AccountsTransfersWebTwoScreen: React.FC<AccountsTransfersProps> = (props) => {
  const { onClose, data, onLayout, account, tab, returnData, refresh, record } = props;
  const { t } = useTranslation('account-transfer');
  const layout = useContext(LayoutContext);
  const {
    topHeader,
    body,
    horizontalWrapper,
    textLabel,
    textDebitAmount,
    textTitle,
    textAtmPin,
    btn,
    textHeaderTitle,
    textHeaderSubTitle
  } = sxStyles;

  const navigation = useNavigation<NativeStackNavigationProp<AccountsTransfersStackParamList>>();

  const [instructionTime, setInstructionTime] = useState(moment().format('YYYY-MM-DD HH:mm:ss'));

  return (
    <View
      sx={{
        height: layout.isMobile ? '100%' : 'auto',
        width: layout.dimensions.width,
        maxWidth: layout.isMobile ? layout.dimensions.width : Math.min(500, layout.dimensions.width),
        maxHeight: layout.dimensions.height,
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <View sx={topHeader}>
        <Icon name="checkCircleBlue" height={90} width={90} />
        <H6 bold sx={textHeaderTitle}>
          {t('account-transfer::instruction-placed.text--account-transfer-placed')}
        </H6>
        <TextCaption sx={textHeaderSubTitle}>{t('account-transfer::instruction-placed.text--you-made-following-transfer')}</TextCaption>
      </View>
      <ScrollView sx={{ flex: 1, backgroundColor: 'transparent' }} nestedScrollEnabled>
        <View sx={body}>
          <View>
            <TextCaption sx={textTitle} semiBold>
              {t('account-transfer::glossary.from-account')}
            </TextCaption>
            <H6 sx={{ ...textAtmPin, mb: '$1' }}>
              {data?.sourceAccount?.accountLabel
                ? t(`earn-invest::glossary.${data?.sourceAccount?.accountLabel}`)
                : data?.sourceAccount?.accountName || ' - '}
            </H6>
            <TextBody>{data.sourceAccount.accountNumber}</TextBody>
          </View>
          <View>
            <TextCaption sx={textTitle} semiBold>
              {t('account-transfer::glossary.to-account')}
            </TextCaption>
            <H6 sx={{ ...textAtmPin, mb: '$1' }}>
              {data?.destinationAccount?.accountLabel
                ? t(`earn-invest::glossary.${data?.destinationAccount?.accountLabel}`)
                : data?.destinationAccount?.accountName || ' - '}
            </H6>
            <TextBody>{data.destinationAccount.accountNumber}</TextBody>
          </View>
          <View>
            <TextCaption sx={textTitle} semiBold>
              {t('account-transfer::glossary.amount')}
            </TextCaption>
            <H6 sx={textAtmPin}>
              {formatAmountByDecimal({ value: data.amount, decimals: 2 })} {data?.asset}
            </H6>
          </View>
          <View>
            <TextCaption sx={textTitle} semiBold>
              {t('account-transfer::glossary.description-optional')}
            </TextCaption>
            <H6 sx={textAtmPin}>{data.description || '-'}</H6>
          </View>
          <View>
            <TextCaption sx={textTitle} bold>
              {t('card-account-management::glossary.xmetadata')}
            </TextCaption>
            {Object.keys(data.xmetadata).map((k) => (
              <View sx={{ pl: '$5', my: '$3' }}>
                <TextCaption sx={textTitle} semiBold>
                  {k}
                </TextCaption>
                <H6 sx={textAtmPin}>{data.xmetadata[k]}</H6>
              </View>
            ))}
          </View>
          <View sx={horizontalWrapper}>
            <TextBody sx={textLabel}>{t('account-transfer::glossary.instruction-time')}</TextBody>
            <TextBody sx={textDebitAmount}>{returnData?.instructionTime || instructionTime || '-'}</TextBody>
          </View>
          <UniButton
            sx={btn}
            labelSx={{ color: '$balck' }}
            label={t('account-transfer::glossary.button-close')}
            onPress={() => {
              refresh && refresh();
              navigation.getParent()?.goBack();
            }}
          />
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  topHeader: {
    justifyContent: 'center',
    alignItems: 'center',
    background: 'primaryDefault',
    mt: '$10'
  },
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28,
    gap: '$5'
  },
  horizontalWrapper: {
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  textLabel: {
    color: '$textSecondary',
    mb: '$1'
  },
  textDebitAmount: {
    mb: '$1',
    color: '$textPrimary'
  },
  textTitle: {
    marginBottom: 4,
    color: '$textSecondary'
  },
  textAtmPin: {
    fontWeight: 'bolder',
    color: '$textThemeSecondary'
  },
  btn: {
    width: '100%',
    height: 44
  },
  textHeaderTitle: {
    mb: '$2'
  },
  textHeaderSubTitle: {
    color: '$textPrimary'
  }
};
